<template>
  <FormStep
    title="Personal Information"
    @submit="handleSubmit"
  >
    <template #description>
      <p
        class="body-md"
        v-text="'Please review and complete any information that is missing or needs to be updated.'"
      />
    </template>

    <BaseFormErrorSummary
      :errors="errors"
      :submit-count="submitCount"
    />

    <BaseInlineNotification
      v-if="submissionError"
      heading-text="An Error Occurred"
      hide-title-element
      type="error"
    >
      <p
        class="body-sm"
        v-text="submissionError"
      />
    </BaseInlineNotification>

    <div class="flex flex-col gap-y-4">
      <BaseCountrySelect
        v-model="values.country"
        label="Country"
      />

      <BaseTextInput
        id="address"
        v-model="values.address"
        autocomplete="street-address"
        helper-text-id="street-address-helper-text"
        label="Address"
        name="address"
        required
        validation-rules="required"
      >
        <template #helperText>
          <p v-text="'Home address required for CE credit'" />
        </template>
      </BaseTextInput>

      <BaseTextInput
        id="addressTwo"
        v-model="values.addressTwo"
        label="Address Line 2"
        name="addressTwo"
      />

      <BaseTextInput
        id="city"
        v-model="values.city"
        label="City"
        name="city"
        required
        validation-rules="required"
      />

      <BaseStateSelect
        v-model="values.state"
        :country-id="values.country"
        :required="hasRegions"
        :validation-rules="hasRegions ? 'required' : ''"
        @regions-change="onRegionsChange"
        @clear-state="setFieldValue('state', '')"
      />

      <BaseTextInput
        id="postcode"
        v-model="values.postcode"
        v-maska="'#####'"
        autocomplete="postal-code"
        inputmode="numeric"
        label="Zip Code"
        name="postcode"
        required
        validation-rules="required"
      />

      <BaseTextInput
        id="phone"
        v-model="values.phone"
        v-maska="'(###) ###-####'"
        autocomplete="tel"
        label="Personal Mobile Phone"
        name="phone"
        required
        type="tel"
        validation-rules="required"
      />

      <BaseTextInput
        id="secondaryPhone"
        v-model="values.secondaryPhone"
        v-maska="'(###) ###-####'"
        autocomplete="off"
        label="Secondary Phone"
        name="secondaryPhone"
        type="tel"
      />
    </div>

    <div class="flex flex-col gap-y-4">
      <h3
        class="wc-overline text-primary-500"
        v-text="'Professional Information'"
      />

      <BaseStateSelect
        id="licenseState"
        v-model="values.licenseState"
        :country-id="values.country"
        label="License State or Province"
        name="licenseState"
        :required="hasLicenseRegions"
        validation-rules=""
        @regions-change="onLicenseRegionsChange"
        @clear-state="setFieldValue('licenseState', '')"
      />

      <div
        v-if="noLicenseTypesError"
        class="body-sm flex items-center gap-2 text-red"
      >
        <IconExclamationCircleFilled
          :size="20"
          aria-hidden="true"
        />
        <span v-text="'Please select a different state, no licenses found.'" />
      </div>

      <BaseSelectInput
        id="licenseType"
        v-model="values.licenseType"
        default-option-text="Select a license type"
        label="License Type"
        name="licenseType"
        required
        :disabled="licenseTypeDisabled"
      >
        <option
          v-for="type in licenseTypes"
          :key="type.id"
          :value="type.id"
          v-text="type.profession_name"
        />
      </BaseSelectInput>

      <BaseTextInput
        id="licenseNumber"
        v-model="values.licenseNumber"
        label="License Number"
        name="licenseNumber"
        :disabled="licenseNumberDisabled"
        maxlength="25"
      />
    </div>

    <template #footer>
      <div class="flex justify-end gap-x-2 p-4">
        <button
          :disabled="isSubmitting"
          class="btn btn--outline"
          data-test-id="submit-and-close-button"
          type="button"
          @click="submitAndClose"
        >
          <span class="relative inline-flex items-center justify-center">
            <BaseLoadingIcon :visible="isLoadingClose" />
            <span
              class="whitespace-nowrap"
              :class="{ 'invisible': isLoadingClose }"
              v-text="'Save & Close'"
            />
          </span>
        </button>
        <button
          :disabled="isSubmitting"
          class="btn btn--primary"
          type="submit"
        >
          <span class="relative inline-flex items-center justify-center">
            <BaseLoadingIcon :visible="isLoading" />
            <span
              class="whitespace-nowrap"
              :class="{ 'invisible': isLoading }"
              v-text="'Save & Continue'"
            />
          </span>
        </button>
      </div>
    </template>
  </FormStep>
</template>

<script lang="ts" setup>
import { onBeforeMount, ref, watch, computed } from 'vue'
import { vMaska } from 'maska/vue'
import { useForm } from 'vee-validate'
import { log } from '@js/utils/logger'
import { accountLinks } from '@js/utils/links'
import { addQueryParamToURL } from '@js/utils/addQueryParamToURL'
import { registerAndUpsertItemToCart } from '@js/api/checkout/registerAndUpsertItemToCart'
import { getCustomerLicenses } from '@js/api/customer/getCustomerLicenses'
import { getCustomerLicenseProfessions } from '@js/api/customer/getCustomerLicenseProfession'
import { upsertCustomerLicense } from '@js/api/customer/upsertCustomerLicense'
import { Cart } from '@js/api/types/cart/Cart'
import { Customer } from '@js/api/types/customer/Customer'
import { CustomerRegistration } from '@js/api/types/customer/CustomerRegistration'

import FormStep from '@js/components/checkout/partials/FormStep.vue'
import BaseStateSelect from '@js/components/global/forms/BaseStateSelect.vue'
import BaseTextInput from '@js/components/global/forms/inputs/BaseTextInput.vue'
import BaseSelectInput from '@js/components/global/forms/inputs/BaseSelectInput.vue'
import BaseCountrySelect from '@js/components/global/forms/BaseCountrySelect.vue'
import BaseInlineNotification from '@js/components/global/BaseInlineNotification.vue'
import BaseFormErrorSummary from '@js/components/global/forms/BaseFormErrorSummary.vue'
import BaseLoadingIcon from '@js/components/global/BaseLoadingIcon.vue'
import useCart from '@js/composables/useCart'
import { IconExclamationCircleFilled } from '@tabler/icons-vue'
import type { CustomerLicenseProfession } from '@js/api/types/customer/CustomerLicenseProfession'

const props = defineProps<{
  cart: Cart | null
  user: Customer | null
  registrationId: number | null
  registration: CustomerRegistration | null
}>()

const emit = defineEmits<{
  nextStep: []
  updateRegistration: [customerRegistration: CustomerRegistration | null]
  cartUpdated: [cart: Cart]
}>()

const {
  errors,
  values,
  setValues,
  resetForm,
  submitCount,
  isSubmitting,
  handleSubmit: handleValidationSubmit,
  setFieldValue,
} = useForm({
  initialValues: {
    country: props.registration?.personal.country || 'US',
    address: props.registration?.personal.address || '',
    addressTwo: props.registration?.personal.address_two || '',
    city: props.registration?.personal.city || '',
    state: props.registration?.personal.state || '',
    postcode: props.registration?.personal.postcode || '',
    phone: props.registration?.personal.phone || '',
    secondaryPhone: props.registration?.personal.secondary_phone || '',
    licenseState: '',
    licenseType: '',
    licenseNumber: '',
  },
})

const { getCartFromStorage, getCartFromMagento } = useCart()

const isLoading = ref(false)
const isLoadingClose = ref(false)

const submissionError = ref('')

const licenseTypes = ref<CustomerLicenseProfession[]>([])
const licenseTypeDisabled = ref(true)
const licenseNumberDisabled = ref(true)
const noLicenseTypesError = ref(false)
// tracks the profession ID to restore on pre-fill after async type load
const pendingLicenseProfessionId = ref<number | null>(null)

const licenseRegionCount = ref(0)
const hasLicenseRegions = computed(() => licenseRegionCount.value > 0)

const regionCount = ref(0)
const hasRegions = computed(() => regionCount.value > 0)

const handleSubmit = handleValidationSubmit(async (): Promise<void> => {
  isLoading.value = true
  const registration = await submitRegistration()

  if (registration) {
    emit('updateRegistration', registration)

    await getCartFromMagento()
    const newCart = getCartFromStorage()

    if (newCart) {
      emit('cartUpdated', newCart)
    }

    emit('nextStep')
  } else {
    submissionError.value = 'An error occurred while submitting the form. Please try again.'

    log({
      message: 'Error handling submit',
      file: 'src/wcei/js/components/checkout/partials/StepPersonalInformation.vue',
    })
  }

  isLoading.value = false
})

const submitAndClose = handleValidationSubmit(async (): Promise<void> => {
  isLoadingClose.value = true

  const registration = await submitRegistration()

  if (registration) {
    emit('updateRegistration', registration)

    window.location.assign(accountLinks.DASHBOARD)
  } else {
    submissionError.value = 'An error occurred while submitting the form. Please try again.'

    log({
      message: 'Error handling submit and closing',
      file: 'src/wcei/js/components/checkout/partials/StepPersonalInformation.vue',
    })
  }

  isLoadingClose.value = false
})

const submitRegistration = async (): Promise<CustomerRegistration | null> => {
  try {
    return await registerAndUpsertItemToCart({
      cartId: props.cart?.id ?? '',
      sku: props.cart?.items[0].product.sku ?? '',
      courseOption: props.registration?.item.course_option ?? '',
      craftEntryId: props.registration?.craft_entry_id ?? 0,
      qty: 1,
      registrationId: props.registrationId,
      personal: {
        upsert_address: true,
        country: values.country,
        address: values.address,
        address_two: values.addressTwo,
        city: values.city,
        state: values.state,
        postcode: values.postcode,
        phone: values.phone,
        secondary_phone: values.secondaryPhone,
      },
      professional: {
        licenses: await (async () => {
          const existingLicenses = await getCustomerLicenses(props.user?.id ?? 0).catch(() => [])
          let licenseIds = existingLicenses.map(l => l.license_id)

          if (values.licenseState && values.licenseType) {
            const professionId = Number(values.licenseType)
            const selectedType = licenseTypes.value.find(t => String(t.id) === String(values.licenseType))

            if (selectedType) {
              try {
                const existingForProfession = existingLicenses.find(l => l.license_profession_id === professionId)
                const customerLicense = await upsertCustomerLicense({
                  customerId: props.user?.id ?? 0,
                  licenseId: existingForProfession?.license_id ?? 0,
                  licenseNumber: values.licenseNumber?.trim() || null,
                  licenseType: selectedType.profession_name,
                  licenseState: values.licenseState,
                  license_profession_id: professionId,
                })
                const withoutNew = licenseIds.filter(id => id !== customerLicense.license_id)
                licenseIds = [...withoutNew, customerLicense.license_id]
              } catch {
                log({ message: 'Failed to upsert license', file: 'src/wcei/js/components/checkout/partials/StepPersonalInformation.vue' })
              }
            }
          }

          return licenseIds
        })(),
      },
    })
  } catch (error) {
    submissionError.value = 'An error occurred while submitting the form. Please try again.'

    log({
      message: 'Error submitting registration',
      file: 'src/wcei/js/components/checkout/partials/StepPersonalInformation.vue',
    })

    return null
  }
}

/**
 * Reload form values when the registration prop changes (e.g. after a previous step saves data).
 */
watch(() => props.registration, (newRegistration, oldRegistration) => {
  if (newRegistration?.personal) {
    const formValues = {
      country: newRegistration?.personal.country || 'US',
      address: newRegistration?.personal.address || '',
      addressTwo: newRegistration?.personal.address_two || '',
      city: newRegistration?.personal.city || '',
      state: newRegistration?.personal.state || '',
      postcode: newRegistration?.personal.postcode || '',
      phone: newRegistration?.personal.phone || '',
      secondaryPhone: newRegistration?.personal.secondary_phone || '',
    }
    if (!oldRegistration) {
      resetForm({ values: formValues })
    } else {
      setValues(formValues)
    }
  }
})

/**
 * Handle when the list of regions changes for the personal address state select
 */
const onRegionsChange = (list: Array<{label: string; value: string}>) => {
  regionCount.value = Array.isArray(list) ? list.length : 0

  if (regionCount.value === 0) {
    setFieldValue('state', '', false)
  }
}

const onLicenseRegionsChange = (list: Array<{label: string; value: string}>) => {
  licenseRegionCount.value = Array.isArray(list) ? list.length : 0
  if (licenseRegionCount.value === 0) {
    setFieldValue('licenseState', '', false)
  }
}

onBeforeMount(async () => {
  addQueryParamToURL('step', '1')

  // Pre-fill license fields from the customer's most recent existing license
  const existingLicenses = await getCustomerLicenses(props.user?.id ?? 0).catch(() => [])
  if (existingLicenses.length > 0) {
    const latest = existingLicenses[0]
    pendingLicenseProfessionId.value = latest.license_profession_id
    if (latest.license_number) {
      setFieldValue('licenseNumber', latest.license_number, false)
    }
    setFieldValue('licenseState', latest.license_state, false)
  }
})

/**
 * Loads license type options and restores a pre-filled type when state is set.
 */
watch(() => values.licenseState, async (newState) => {
  noLicenseTypesError.value = false
  licenseTypes.value = []

  if (!pendingLicenseProfessionId.value) {
    setFieldValue('licenseType', '', false)
    setFieldValue('licenseNumber', '', false)
    licenseNumberDisabled.value = true
  }

  if (!newState) {
    licenseTypeDisabled.value = true
    return
  }

  try {
    const professions = await getCustomerLicenseProfessions(newState)
    licenseTypes.value = professions

    if (professions.length === 0) {
      noLicenseTypesError.value = true
      licenseTypeDisabled.value = true
    } else {
      licenseTypeDisabled.value = false

      if (pendingLicenseProfessionId.value) {
        const match = professions.find(t => t.id === pendingLicenseProfessionId.value)
        if (match) {
          setFieldValue('licenseType', String(match.id), false)
          licenseNumberDisabled.value = false
        }
        pendingLicenseProfessionId.value = null
      }
    }
  } catch {
    licenseTypeDisabled.value = true
  }
}, { immediate: true })

watch(() => values.licenseType, (newType) => {
  licenseNumberDisabled.value = !newType
})
</script>
